import type { GridExportItem } from 'stencil-grid-builder/types';
import type { FacetItem } from 'ui-common';
export type { GridItemLayout } from 'stencil-grid-builder/types';
import type { GridLayoutConfig } from '../components/lw-template/interfaces';

export interface EmbedsASConfig {
  target?: string;
}

export enum ASTemplateType {
  SEARCH = 'SEARCH',
  TYPEAHEAD = 'TYPEAHEAD',
  PRODUCT_QA = 'PRODUCT_QA',
  CONVERSATIONAL_QA = 'CONVERSATIONAL_QA',
  CONVERSATIONAL_QA_FAQ = 'CONVERSATIONAL_QA_FAQ',
}

export enum ASTemplateComponentType {
  ResultsList = 'RESULTS_LIST_SEARCH',
  FACETS = 'FACETS_SEARCH',
  TYPEAHEAD = 'SEARCHBOX',
  QnA = 'QA_PAIR',
  CONVERSATIONAL_QNA = 'CONVERSATIONAL_QA',
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  BUTTON = 'BUTTON',
}

export interface ASTemplate {
  id: string;
  type: ASTemplateType;
  name: string;
}


// Navigation state for a single prefix/namespace.
export interface PrefixNavState {
  searchTerms?: string;
  searchWithinTerms?: string[];
  filters: RequestFilter[];
  offset?: number;
  pageSize?: number;
  sort?: SortConfig[];
}

export interface EmbedsASStoreConfig {
  id: string;
  mock?: boolean;
  /**
   * Fully-qualified URL the store should GET for render requests instead of POSTing to
   * `${apiService.serviceHost}/templates/${id}/render`. Used when the consumer wants the
   * store's re-renders to hit a different endpoint than the default — e.g. published
   * compositions on `*.ui.lucidworks.dev` where only the public GET endpoint is exposed.
   * When set, navigation state is serialized into query params matching the public
   * composition render contract.
   *
   * Must NOT contain a query string. The store appends `?${navStateParams}` directly,
   * so an existing `?` in the URL would produce a malformed `...?existing=1?new=2`.
   */
  renderUrl?: string;
  /**
   * Optional callback to fetch Bearer token for authenticated API requests (e.g., datastore configuration).
   * Returns a Promise resolving to the raw token value (no "Bearer" prefix).
   */
  getBearerToken?: () => Promise<string>;
  /**
   * Optional array of datastore objects (from ui-builder flow).
   * If provided, typeahead embed tokens will be extracted directly from this array
   * instead of making API calls to fetch datastore configuration.
   */
  datastores?: any[];
}

/**
 * Internal store state - tracks parameters by prefix.
 * The key is the prefix string ('' for global/default namespace).
 */
export interface EmbedsASStoreRequestParams {
  includeConfig: boolean;
  prefixState: { [prefix: string]: PrefixNavState };
}

export interface ExtractionConfig {
  "query-params"?: string;
  selector?: string;
}

export interface DatastoreQuery {
  id: string;
  datastoreId?: string;
  name?: string;
  prefix?: string;
}

export interface EmbedsASTemplateRenderData {
  template: {
    id?: string;
    config: {
      layout?: GridLayoutConfig;
      extraction?: Record<string, ExtractionConfig>;
      type?: string;
      datastoreQueries?: DatastoreQuery[];
    };
  };

  components: EmbedsASTemplateRenderComponent[];
}

export interface EmbedsASTemplateConversationalQnARenderStreaming {
  productIdHash: string,
  indexTarget: string,
  datasources: string[],
  conversationModelId: string
}

export interface EmbedsASTemplateRenderComponent {
  id: string;

  // These are required on the request shape (`ASTemplateRequestComponent`) but
  // stripped from the render response. Typed here as optional so consumers (e.g.
  // ui-builder's draft store) can preserve the request-side values across
  // preview round-trips without resorting to type assertions.
  name?: string;
  datastoreQueryId?: string;

  result?: {
    offset?: number;
    pageSize?: number;
    total?: number;
    records?: RecordsResponse;
    facets?: FacetResult[];
    pairs?: QAResponsePair[];
    conversationRequest?: EmbedsASTemplateConversationalQnARenderStreaming
  };

  type: string;
  config?: {
    prefix?: string;  // Read prefix - which datastore query this component reads from
    affectedPrefixes?: string[];  // Write prefixes - which prefixes this component writes to
    componentLayoutConfig?: LayoutConfig;
    cardMappings?: CardMappings;
    beaconFieldMapping?: BeaconFieldMapping;
    facets?: FacetConfig[];
    agentConfigurationId?: string;  // Agent configuration ID for conversational QnA
    highlightingEnabled?: boolean;
    [key: string]: any; // to temporarily support legacy typeahead config
  }

}

export interface EmbedsASTemplateTypeaheadRenderComponent extends EmbedsASTemplateRenderComponent {
  config: EmbedsASTemplateRenderComponent['config'] & {
    typeaheadTemplateId?: string;
    typeaheadEndpoint?: string;
    navigateOnSubmit?: boolean;
    pagePath?: string;
    openInNewTab?: boolean;
  };
}

export interface EmbedsASTemplateQnARenderComponent extends EmbedsASTemplateRenderComponent {
  config?: EmbedsASTemplateRenderComponent['config'] & {
    title?: string;
    displayMode?: QnADisplayMode;
    placeholderText?: string;
  };
}

export interface EmbedsASTemplateTextRenderComponent extends EmbedsASTemplateRenderComponent {
  config?: EmbedsASTemplateRenderComponent['config'] & {
    text?: string;
  };
}

export interface EmbedsASTemplateImageRenderComponent extends EmbedsASTemplateRenderComponent {
  config?: EmbedsASTemplateRenderComponent['config'] & {
    imageURL: string;
    fallbackImageURL?: string;
    altText?: string;
    clickThroughURL?: string;
    clickThroughTarget?: '_blank' | '_self';
  };
}

export interface EmbedsASTemplateButtonRenderComponent extends EmbedsASTemplateRenderComponent {
  config?: EmbedsASTemplateRenderComponent['config'] & {
    name: string;
    buttonType: 'LINK' | 'ACTION';
    pagePath?: string;
    openInNewTab?: boolean;
    referencedComponentId?: string;
    tokens?: Array<{ tokenName: string; referencedFieldName: string }>;
    actionKind?: 'EXPORT' | 'API_CALL';
  };
  result?: EmbedsASTemplateRenderComponent['result'] & {
    tokens?: Record<string, string>;
  };
}

// =====================================
// QNA interfaces
// =====================================

// API response format for QA pairs
export interface QAResponsePair {
  type: string;
  cardMappingKey: string;
  question: string;
  answer: string;
  confidence?: number;
}

// Simplified version of QAResponsePair used within the component.
export interface QnAPair {
  question: string;
  answer: string;
  confidence?: number;
}

export interface QnAMessage {
  messageType: 'question' | 'answer';
  text: string;
}

export enum QnADisplayMode {
  FULL = 'full',
  SEARCH_ONLY = 'searchOnly',
  QUESTIONS_ONLY = 'questionsOnly'
}

export enum QnAContextType {
  PRODUCT = 'product',
  SEARCH = 'search',
  STANDALONE = 'standalone'
}

// =====================================
// Template request interfaces
// =====================================

export interface Card {
  id: string; //UUID
  type: string;
  fallback: boolean;
  fields: FieldConfig[];
  idFieldName: string;
}

export interface ASTemplateRequestComponent {
  id: string; //UUID
  type: string;
  name: string;
  // Only ResultsList and Facets require this on the BE; other component types
  // (Searchbox, Image, Text, Button) accept the request without it.
  datastoreQueryId?: string;
  componentLayoutConfig?: LayoutConfig;
  // Required (non-empty) on the BE for ResultsList only.
  cards?: Card[];
  // Required (non-empty) on the BE for Facets only.
  facets?: FacetConfig[];
  // Searchbox only: when true, the BE requires `pagePath` to be non-empty.
  navigateOnSubmit?: boolean;
  // Required (non-empty) on the BE for Searchbox only, when `navigateOnSubmit` is true.
  pagePath?: string;
}

export interface ASTemplateRequest {
  id: string;
  name: string;
  type: ASTemplateType;
  createdBy: string;
  creationDate: string;
  lastModifiedBy: string;
  lastModified: string;
  layout?: LayoutConfig;
  extraction: Record<string, ExtractionConfig>;
  datastoreQueries: DatastoreQuery[];
  components: ASTemplateRequestComponent[];
}

// =====================================
// Preview API interfaces
// =====================================

/**
 * Template data for preview requests
 */
export interface PreviewTemplateRequest {
  id?: string;
  initialId?: string;
  versionNumber?: number;
  changeMetadata?: string;
  type: string;
  name: string;
  layout?: any;
  extraction?: Record<string, ExtractionConfig>;
  datastoreQueries?: DatastoreQuery[];
  components?: any[];
}

/**
 * Preview request payload for POST /templates/{id}/preview
 */
export interface PreviewRequest {
  template: PreviewTemplateRequest;
  navState?: { [prefix: string]: PrefixNavState };
  includeConfig?: boolean;
  searchFacets?: boolean;
}

/**
 * Preview response from POST /templates/{id}/preview
 * This matches the EmbedsASTemplateRenderData structure
 */
export type PreviewResponse = EmbedsASTemplateRenderData;

// =====================================
// Render response interfaces
// =====================================

export enum FieldAction {
  NONE = 'NONE',
  URL = 'URL',
  IMAGE = 'IMAGE',
  FILTER = 'FILTER'
}

export enum LayoutType {
  LIST_VIEW = 'list-view',
  GRID_VIEW = 'grid-view',
  TABLE_VIEW = 'table-view',
  DETAIL_VIEW = 'detail-view'
}

export interface FieldConfig {
  id: string; //UUID
  fieldName: string;
  displayName: string;
  enabled: boolean;
  action: FieldAction;
}

export enum FieldDisplayType {
  TEXT = 'text',
  IMAGE = 'image',
  DATE = 'date',
  PRICE = 'price',
  HYPERLINK = 'hyperlink',
}

export type FieldLayoutMode = 'inline' | 'stack';

export interface ColumnFieldStyling {
  fieldName: string;
  columnWidth?: string;
  displayType?: FieldDisplayType;
  inlineFieldLink?: InlineFieldLinkConfig;
  fieldActionLinks?: FieldActionLinkConfig[];
  actionLinkLayout?: 'vertical' | 'horizontal';
}

export interface LabelStyling extends Partial<FontConfig> {
  color?: string;
}

export interface ResultFieldStyling {
  fieldName: string;
  displayType?: FieldDisplayType;
  maxLines?: number | null;
  layoutMode?: FieldLayoutMode;
  showLabel?: boolean;
  inlineFieldLink?: InlineFieldLinkConfig;
  fieldActionLinks?: FieldActionLinkConfig[];

  // Text/date/price/hyperlink styling
  font?: FontConfig;
  color?: string;
  fieldNameColor?: string;

  // Image styling
  width?: number;
  height?: number;
  borderRadius?: number;
  borderColor?: string;
  backgroundColor?: string;
}

/**
 * Base configuration for field URL linking with token replacement
 * Shared by both inline field links and field action links
 */
export interface BaseFieldLinkConfig {
  /** URL template with {token} placeholders */
  urlTemplate: string;

  /**
   * Maps token names to field names in record
   * If not provided, token names match field names
   */
  tokenFieldMap?: { [tokenName: string]: string };
  openInNewTab?: boolean;

  /**
   * When true, indicates the field value is itself a URL and should not be encoded.
   * Prevents encodeURIComponent from breaking URLs used as token values.
   * Only use this flag when the template is a single-token URL (e.g. {field_url}).
   * When the template contains additional tokens alongside the URL token,
   * those tokens will also be left unencoded.
   */
  valueIsUrl?: boolean;
}

/**
 * Configuration for inline field links
 * The field value itself becomes clickable
 */
export interface InlineFieldLinkConfig extends BaseFieldLinkConfig {
  /**
   * Text to display for clickable link (supports tokens)
   * If not provided, uses field value
   */
  linkText?: string;
}

/**
 * Configuration for action links that appear alongside field values
 * Examples: "Where used", "View details", "Download"
 */
export interface FieldActionLinkConfig extends BaseFieldLinkConfig {
  /**
   * Text to display for the action link (required)
   * Example: "Where used", "View details", "Download"
   */
  linkText: string;

  /** How to display the action: 'link' (default) or 'button' */
  displayType?: 'link' | 'button';

  /**
   * Type of field action link for categorization and conditional behavior
   * Examples: 'whereUsed', 'viewDetails', 'download', 'export'
   * Can be used for future conditional styling, and custom handling
   */
  fieldActionLinkType?: string;

  /**
   * Separator between field value and action link
   * Default: " | "
   * Examples: " • ", " - ", " → "
   */
  separator?: string;
}

export interface FieldStylingConfig {
  fieldStyling?: ResultFieldStyling[];
}

export interface ListConfig extends FieldStylingConfig {
  showLabels?: boolean;
  labelStyling?: LabelStyling;
  resultStyling?: ResultStyling;
  listStyling?: ListStyling;
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
  NONE = ''
}

export interface SortConfig {
  label: string;
  field: string;
  direction: SortDirection;
  order: number;
}

export interface NavStateSortConfig {
  property: string;
  direction: string;
}

export interface PageableConfig {
  offset: number;
  pageSize: number;
  pagination: boolean;
  sort: SortConfig[];
}

export interface FontConfig {
  family: string[];
  weight: string;
  size: number;
}

export interface TableWidthConfig {
  fixed: {
    enabled: boolean;
    width: string;
  };
}

export interface ColumnConfig {
  resizableColumns: boolean;
  fieldStyling: ColumnFieldStyling[];
}

export interface RowConfig {
  stickyHeader: boolean;
  rowHeaderFont: FontConfig;
  rows: {
    striping: boolean;
  };
}

export interface TableStyling {
  textAlignment: string;
  tableFont: FontConfig;
  tableBorders: boolean;
  borderColor: string;
  backgroundColor: string;
}

export interface TableConfig {
  columnConfig: ColumnConfig;
  rowConfig: RowConfig;
  tableWidth: TableWidthConfig;
  tableStyling: TableStyling;
  rowPageLink?: InlineFieldLinkConfig;
}

// Conditional row coloring interfaces
export enum ConditionalRowColoringOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH'
}

export interface ConditionalRowColoringCondition {
  columnName: string;
  operator: string;
  value: string;
}

export interface ConditionalRowColoringStyling {
  backgroundColor: string;
  textColor: string;
}

export interface ConditionalRowColoringRule {
  id: string;
  enabled: boolean;
  condition: ConditionalRowColoringCondition;
  styling: ConditionalRowColoringStyling;
}

export interface ConditionalRowColoring {
  rules: ConditionalRowColoringRule[];
}

export interface AdvancedRowConfig {
  conditionalRowColoring: ConditionalRowColoring;
}

export interface AdvancedTableConfig {
  rowConfig: AdvancedRowConfig;
}

export interface ResultStyling {
  backgroundColor?: string;
  padding?: number;
  borderRadius?: number;
  fieldGap?: number;
  hoverBackgroundColor?: string;
}

export interface ListStyling {
  resultGap?: number;
}

// Layout configuration interfaces
export interface BasicLayoutConfig {
  selectedLayout?: LayoutType;
  defaultResultCount?: number;
  layoutType?: LayoutType[];
  resultsCount?: number[];
  pageable?: PageableConfig;
  tableConfig?: TableConfig;
  listConfig?: ListConfig;
  detailConfig?: DetailConfig;
  facetsTitle?: string;
  searchableFacetValue?: boolean;
  collapsible?: boolean;
  inclusiveExclusive?: boolean;
  selectedFacetPills?: boolean;
  facetStyling?: FacetStylingConfig;
  searchBarColor?: string;
  placeHolder?: SearchBarConfig;
  typeAheadConfig?: TypeAheadConfig;
  showQueryChip?: boolean;
  queryPillStyling?: QueryPillStyling;
  showSort?: boolean;
  showPagination?: boolean;
  showSearchWithin?: boolean;
  searchWithinMaxTerms?: number;
  emptyStateAlignment?: 'top' | 'center';
  emptyStateTopOffset?: number;
  textStyling?: TextStyling;
  imageStyling?: ImageStyling;
  highlightingStyling?: HighlightingStyling;
  buttonStyling?: ButtonStyling;
  searchButtonConfig?: SearchBarSubmitButtonConfig;
  fixedHeight?: boolean;

  // optional labels for accessibility (used for aria-labelledby)
  searchBarLabel?: string;
  resultsSortLabel?: string;
  resultsPaginationLabel?: string;
  resultsExportLabel?: string;
}

export interface ExportConfig {
  enabled?: boolean;        // Results-list export feature toggle
  limit?: number;           // Max records exported via the backend "Export All" path; clamped to MAX_EXPORT_LIMIT
}

export interface HighlightingStyling {
  primaryColor?: string;
  secondaryColor?: string;
}

export interface AdvancedLayoutConfig {
  tableConfig?: AdvancedTableConfig;
}

// Derived from library type for automatic compatibility with grid-viewer
export type GridPosition = Pick<GridExportItem, 'canvasId' | 'layouts'> & { zIndex?: number };

export interface LayoutConfig {
  gridPosition?: GridPosition;
  basicLayoutConfig?: BasicLayoutConfig;
  advancedLayoutConfig?: AdvancedLayoutConfig;
  exportConfig?: ExportConfig;
  highlightingEnabled?: boolean;
}

export interface CardFieldMapping {
  displayName: string;
  action: FieldAction;
  enabled: boolean;
  inlineFieldLink?: InlineFieldLinkConfig;
}

export interface CardMappings {
  fallback: {
    type: string;
    fields?: Record<string, CardFieldMapping>;
  };
}

export interface BeaconFieldMapping {
  id?: string;
  title?: string;
  price?: string;
  image?: string;
  url?: string;
}

export interface SortResponse {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}

export interface PageableResponse {
  pageNumber: number;
  pageSize: number;
  sort: SortResponse;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface RecordsResponse {
  content: EmbedsASTemplateRecord[];
  pageable: PageableResponse;
  last: boolean;
  totalPages: number;
  totalElements: number;
  first: boolean;
  size: number;
  number: number;
  sort: SortResponse;
  numberOfElements: number;
  empty: boolean;
}

export interface EmbedsASTemplateRecord {
  cardMappingKey: string;
  type: string;
  id: string;
  fields: Record<string, string[]>;
  highlighting?: Record<string, string[]>;
}
export interface EmbedsASStoreRequestBody {
  includeConfig: boolean;
  searchFacets?: boolean;
  navState: {
    [key: string]: EmbedsASNavStateEntry;
  };
}

export interface FacetsSearchFilter {
  field: string;
  term?: string;
  limit?: number;
  ignoreCase?: boolean;
}

export interface EmbedsASNavStateEntry {
  offset?: number;
  pageSize?: number;
  searchTerms?: string;
  searchWithinTerms?: string[];
  filters: RequestFilter[];
  sort?: NavStateSortConfig[];
  facetsSearchFilter?: FacetsSearchFilter;
}

export interface RequestFilter {
  field: string;
  values: string[];
  inclusive?: boolean;
}

export interface FacetConfig {
  fieldName: string;
  displayName: string;
  multiSelect?: boolean;
}

export interface FacetResultValue {
  value: string;
  count: number;
}

export interface FacetResult {
  fieldName: string;
  values: FacetResultValue[];
}

export interface FacetStylingConfig {
  facetFieldFont: FontConfig;
  facetValueFont: FontConfig;
  facetSearchFont?: FontConfig;
  selectedFiltersHeaderStyling?: {
    textColor?: string;
    linkColor?: string;
    font?: FontConfig;
  };
  selectedFilterPillStyling?: {
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    iconColor?: string;
    font?: FontConfig;
  };
  facetColors: {
    background: string;
    hover?: string;
    facetFieldFont: string;
    facetFieldBackground?: string;
    facetFieldBorderColor?: string;
    facetValueFont: string;
    searchText?: string;
    searchBackground?: string;
  };
  facetsPanelTitleStyling?: {
    font?: FontConfig;
    textColor?: string;
    backgroundColor?: string;
    borderColor?: string;
  };
  inclusiveExclusiveButtonStyling?: {
    backgroundColor?: string;
    borderColor?: string;
    activeColor?: string;
    activeIconColor?: string;
  };
}

export interface TextStyling {
  textFont?: FontConfig;
  textColor?: string;
  italic?: boolean;
  underline?: boolean;
  textAlign?: 'Left' | 'Center' | 'Right';
  textPaddingTop?: number;
  textPaddingLeft?: number;
}

export interface ImageStyling {
  /** 'cover' fills the container (may crop), 'contain' fits entirely within it (may letterbox) */
  objectFit?: 'cover' | 'contain';
  borderRadius?: number;
}

export interface ButtonStyling {
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderRadius?: number;
  textFont?: FontConfig;
}

export interface QueryPillStyling {
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  iconColor?: string;
  font?: FontConfig;
}

// =====================================
// Pagination interfaces
// =====================================

export interface PaginationMeta {
  start: number;
  rows: number;
  numFound: number;
}

export type SimplifiedPaginationMode = 'simple' | 'start' | 'early-middle' | 'later-middle' | 'end';

// =====================================
// Detail View interfaces
// =====================================

export interface DetailStyling {
  titleFont?: FontConfig;
  titleColor?: string;
  labelFont?: FontConfig;
  valueFont?: FontConfig;
  labelColor?: string;
  valueColor?: string;
  borderColor?: string;
  linkColor?: string;
}

export interface DetailConfig extends FieldStylingConfig {
  titleFieldName?: string;
  labelWidth?: string;
  detailStyling?: DetailStyling;
}

export interface DetailCellData {
  text: string;
  isHyperlink?: boolean;

  // Advanced linking support
  /** Original record data (needed for URL token replacement) */
  record?: EmbedsASTemplateRecord;
  fieldName?: string;
  linkConfig?: {
    inlineFieldLink?: InlineFieldLinkConfig;
    fieldActionLinks?: FieldActionLinkConfig[];
    actionLinkLayout?: 'vertical' | 'horizontal';
  };
}

export interface DetailRowData {
  label: string;
  cellData: DetailCellData;
  fieldName?: string;
}

// =====================================
// Search Bar/Typeahead interfaces
// =====================================
export interface SearchBarStylingConfig {
  placeHolderFont: FontConfig;
  placeHolderTextColor: string;
}

export interface SearchBarConfig {
  placeHolderText: string;
  placeHolderTextStyles?: SearchBarStylingConfig;
}

export interface SearchBarSubmitButtonStyling {
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  font?: FontConfig;
}

export interface SearchBarSubmitButtonConfig {
  enabled?: boolean;
  text?: string;
  styling?: SearchBarSubmitButtonStyling;
}

export const DEFAULT_SEARCH_CHARACTER_COUNT = 3;
export const DEFAULT_SUGGESTION_COUNT = 10;

export const DEFAULT_SEARCH_BAR_PLACEHOLDER_TEXT = 'Search anything...';

export interface TypeAheadStylingConfig {
  typeAheadItemFont: FontConfig;
  typeAheadColors: {
    typeAheadBackground: string;
    itemHoverBackground: string;
    itemSelectedBackground: string;
    itemActiveBackground: string;
    itemTextColor: string;
  };
}

export interface TypeAheadConfig {
  typeAheadEnabled: boolean;
  suggestionCount?: number;
  searchCharacterCount?: number | number[];
  typeAheadStyling?: TypeAheadStylingConfig;
  typeaheadEndpoint?: string;
}

// System-only filter fields that should be preserved when clearing user filters.
export const SYSTEM_FILTER_FIELDS = ['productIdHash'];

// URL parameter name configuration for deep linking.
export interface UrlParamNames {
  query: string;        // Search query term (default: 'q')
  filter: string;       // Filter constraints, format: `field:value` (default: 'f')
  page: string;         // Page number, 1-indexed (default: 'p')
  pageSize: string;     // Results per page (default: 'rpp')
  sort: string;         // Sort field with direction: `+field` or `-field` (default: 's')
  searchWithin: string; // Search-within terms, multi-value (default: 'sw')
}

export interface AgentResponseCompleteEventDetail {
  question: string;
  response: string;
  sourceId: string;
  questionPosition?: number; // Optional position (1-based) of the question in the predefined questions list
}

// =====================================
// Composition interfaces
// =====================================

/**
 * Asset file (logo, favicon, etc.)
 */
export interface CompositionAsset {
  id: string;
  name: string;
  mimeType: string;
  fileSize: number;
  publicUrl: string;
}

/**
 * Represents a composition - a configured page/application view
 */
export interface Composition {
  id: string;
  name: string;
  description?: string;
  initialId?: string;
  versionNumber?: number;
  published?: boolean;
  restored?: boolean;
  changeMetadata?: string;
  hostProvider?: string;
  uiAddress?: string;
  createdBy?: string;
  creationDate?: string;
  lastModifiedBy?: string;
  lastModified?: string;
  lastPublishedBy?: string;
  lastPublished?: string;
  publishedPath?: string;
  applicationId?: string;
  logo?: CompositionAsset;
  favicon?: CompositionAsset;
  language?: string;
  timezone?: string;
  dateTimeFormat?: string;
}

/**
 * Request body for creating or updating a composition
 */
export interface CompositionRequest {
  initialId?: string;
  versionNumber?: number;
  changeMetadata?: string;
  name?: string;
  description?: string;
}

/**
 * Publish metadata for public access configuration
 */
export interface CompositionPublishMetadataRequest {
  clientName: string;
  compositionName: string;
  title: string;
  description?: string;
  urlSlug?: string;
}

export interface CompositionPublishMetadata {
  id: string;
  compositionId: string;
  clientName: string;
  compositionName: string;
  title: string;
  description?: string;
  host: string;
  createdBy: string;
  creationDate: string;
}

// =====================================
// Composition Builder interfaces
// =====================================

export interface ColorTheme {
  primaryBackground?: string;
  secondaryBackground?: string;
  titles?: string;
  subtitles?: string;
  bodyText?: string;
  secondText?: string;
  linksAndActions?: string;
  regularButtonType?: string;
  regularButtonFill?: string;
  hoverButtonType?: string;
  hoverButtonFill?: string;
}

export interface FontStyle {
  fontFamily?: string;
  fontWeight?: string;
  fontSize?: number;
}

export interface TextTheme {
  title?: FontStyle;
  subtitle?: FontStyle;
  bodyText?: FontStyle;
  secondText?: FontStyle;
}

export interface Theme {
  colorTheme?: ColorTheme;
  textTheme?: TextTheme;
}

export interface OIDCConfiguration {
  clientId?: string;
  clientSecret?: string;
  issuerUri?: string;
  authorizationUri?: string;
  tokenUri?: string;
  jwksUri?: string;
  userinfoUri?: string;
  endSessionEndpoint?: string;
  scopes?: string[];
}

export interface SSOConfiguration {
  protocol?: 'OIDC';
  oidc?: OIDCConfiguration;
  sessionDurationHours?: number;
  sessionExtensionThresholdMinutes?: number;
}

export interface AuthenticationSettings {
  method?: 'SINGLE_SIGN_ON';
  enabled?: boolean;
  sso?: SSOConfiguration;
}

/**
 * Template configuration for a page
 * TODO: See if this is required or if we can use an existing interface
 */
export interface CompositionPageTemplate {
  [key: string]: any;
}

export interface CompositionPageData {
  /** DB UUID — optimistic uuidv4() until the backend assigns the final ID */
  id?: string;
  name: string;
  url: string;
  homePage?: boolean;
  template: CompositionPageTemplate;
  subpages?: CompositionPageData[];
}

export interface CompositionBuilderRequest {
  initialId?: string;
  versionNumber?: number;
  changeMetadata?: string;
  name: string;
  description?: string;
  publishedPath?: string;
  hostProvider?: string;
  uiAddress?: string;
  logoId?: string;
  faviconId?: string;
  language?: string;
  timezone?: string;
  dateTimeFormat?: string;
  theme?: Theme;
  authSettings?: AuthenticationSettings;
  pages?: CompositionPageData[];
  signalStoreId?: string;
}

// Local type for `mo-facet` refs — ui-common's published `types` entry doesn't expose `HTMLMoFacetElement`.
export interface MoFacetElement extends HTMLElement {
  replaceFacets?: (newFacets: FacetItem[]) => Promise<void>;
  clearSearchBar?: () => void;
}